import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTenantStore } from '../../stores/tenant.store';
import { evaluationStandardApi } from '../../lib/api/evaluation-standard.api';
import {
  socraticApi,
  type PracticeStudentSummaryDto,
  type PracticeTrajectoryDto,
  type CalibrationSummaryDto,
  type StudentPracticeAssessmentDto,
  type GrowthDimensionScoreDto,
  type SocraticGrowthDimensionKey,
} from '../../lib/api/socratic.api';
import { SocraticGrowthPanel } from '../portal-v2/components/SocraticGrowthPanel';
import { formatDate } from '../../lib/i18n-format';
import { errorText } from '../../lib/error-text';

interface StudentRow extends PracticeStudentSummaryDto {
  name: string;
}

// Staff view of practice growth + calibration (3c-4 F4). The teacher reviews a
// student's trajectory and can OVERRIDE a dimension level (calibration) — the
// agreement rate LLM↔teacher is the module's "comprobable" validity signal.
export function PracticeProgressPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [students, setStudents] = useState<StudentRow[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [topicTitles, setTopicTitles] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentTenantId) return;
    let alive = true;
    (async () => {
      try {
        const [{ students: raw }, stds] = await Promise.all([
          socraticApi.listPracticeStudents({ tenantId: currentTenantId }),
          evaluationStandardApi
            .list({ tenantId: currentTenantId })
            .catch(() => ({ standards: [] })),
        ]);
        const titles: Record<string, string> = {};
        for (const s of stds.standards) titles[s.id] = s.title;
        // El nombre lo trae el servidor (PR 3A), junto con el alcance: el
        // docente ve a los alumnos de sus secciones, el mentor a sus
        // mentoreados. Antes se leía cada ficha de /people desde acá.
        const rows: StudentRow[] = raw.map((s) => ({
          ...s,
          name: s.studentName || t('socraticReview.student', { uid: s.studentUid.slice(0, 8) }),
        }));
        if (!alive) return;
        rows.sort((a, b) => a.name.localeCompare(b.name));
        setTopicTitles(titles);
        setStudents(rows);
        setSelected((prev) => prev ?? rows[0]?.studentUid ?? null);
      } catch (e) {
        if (alive) setError(errorText(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [currentTenantId, t]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700}>
        {t('practiceProgress.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('practiceProgress.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {students === null && !error && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      )}
      {students !== null && students.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('practiceProgress.empty')}
        </Typography>
      )}

      {students !== null && students.length > 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Paper
            variant="outlined"
            sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, borderRadius: 2 }}
          >
            <Typography variant="subtitle2" sx={{ px: 1.5, py: 1.5 }}>
              {t('practiceProgress.students')} ({students.length})
            </Typography>
            <Divider />
            <Stack>
              {students.map((s) => {
                const active = s.studentUid === selected;
                return (
                  <Box
                    key={s.studentUid}
                    onClick={() => setSelected(s.studentUid)}
                    sx={{
                      px: 1.5,
                      py: 1,
                      cursor: 'pointer',
                      borderLeft: '3px solid',
                      borderLeftColor: active ? 'primary.main' : 'transparent',
                      bgcolor: active
                        ? (th) => alpha(th.palette.primary.main, 0.08)
                        : 'transparent',
                    }}
                  >
                    <Typography variant="body2" fontWeight={active ? 700 : 400} noWrap>
                      {s.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('practiceProgress.assessedN', { n: s.assessed })}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Paper>

          {selected && (
            <StudentGrowth key={selected} studentUid={selected} topicTitles={topicTitles} />
          )}
        </Stack>
      )}
    </Box>
  );
}

function StudentGrowth({
  studentUid,
  topicTitles,
}: {
  studentUid: string;
  topicTitles: Record<string, string>;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [trajectory, setTrajectory] = useState<PracticeTrajectoryDto | null>(null);
  const [calibration, setCalibration] = useState<CalibrationSummaryDto | null>(null);
  const [assessments, setAssessments] = useState<StudentPracticeAssessmentDto[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    if (!currentTenantId) return;
    setLoading(true);
    socraticApi
      .getStudentGrowth({ tenantId: currentTenantId, studentUid })
      .then((r) => {
        setTrajectory(r.trajectory);
        setCalibration(r.calibration);
        setAssessments(r.assessments);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [currentTenantId, studentUid]);

  const agreementLabel = useMemo(() => {
    if (!calibration || calibration.agreementRate === null) return null;
    return Math.round(calibration.agreementRate * 100);
  }, [calibration]);

  const saveOverride = async (
    sessionId: string,
    key: SocraticGrowthDimensionKey,
    level: number | null,
  ) => {
    if (!currentTenantId) return;
    await socraticApi.saveGrowthOverride({
      tenantId: currentTenantId,
      sessionId,
      dimensionKey: key,
      level,
    });
    load();
  };

  if (loading && !trajectory) {
    return (
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <SocraticGrowthPanel trajectory={trajectory} topicTitles={topicTitles} />

      {agreementLabel !== null && (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={t('practiceProgress.agreement', {
            pct: agreementLabel,
            n: calibration!.overridden,
          })}
          sx={{ mb: 2 }}
        />
      )}

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {t('practiceProgress.sessions')}
      </Typography>
      <Stack spacing={2}>
        {assessments.map((a) => (
          <Paper key={a.sessionId} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {topicTitles[a.standardId] ?? a.standardId.slice(0, 8)} · {formatDate(a.assessedAt)}
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {a.dimensions.map((d) => (
                <OverrideRow
                  key={d.key}
                  dim={d}
                  onChange={(level) => saveOverride(a.sessionId, d.key, level)}
                />
              ))}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}

function OverrideRow({
  dim,
  onChange,
}: {
  dim: GrowthDimensionScoreDto;
  onChange: (level: number | null) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const value = dim.teacherOverride ?? 'agree';
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ flex: 1 }}>
          {t(`socraticPractice.progress.dim.${dim.key}`)}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={t('practiceProgress.model', {
            level: t(`socraticPractice.progress.level.${dim.level}`),
          })}
        />
        <Select
          size="small"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === 'agree' ? null : Number(v));
          }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="agree">{t('practiceProgress.agree')}</MenuItem>
          {[1, 2, 3, 4].map((l) => (
            <MenuItem key={l} value={l}>
              {t('practiceProgress.correctTo', {
                level: t(`socraticPractice.progress.level.${l}`),
              })}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 0.5 }}>
        “{dim.evidenceQuote}”
      </Typography>
    </Box>
  );
}
